import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin } from 'lucide-react';
import { events as allEvents } from '../data/siteData';

const EventsPage = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('featured');
  const [heights, setHeights] = useState({});
  const cardRefs = useRef({});

  // Разделяме събитията на 3 групи:
  // 1. Актуални/Моментни (isFeatured: true)
  const featuredEvents = allEvents.filter(event => event.isFeatured === true);
  
  // 2. Предстоящи (датата е в бъдещето и не са featured)
  const parseEventDate = (dateStr) => {
    if (dateStr.includes('-') && dateStr.split('-')[0].length === 4) {
      return new Date(dateStr);
    }
    
    const months = {
      'Януари': 0, 'Февруари': 1, 'Март': 2, 'Април': 3, 'Май': 4, 'Юни': 5,
      'Юли': 6, 'Август': 7, 'Септември': 8, 'Октомври': 9, 'Ноември': 10, 'Декември': 11,
    };
    
    const parts = dateStr.split(' ');
    if (parts.length >= 3) {
      const day = parseInt(parts[0]);
      const month = months[parts[1]];
      const year = parseInt(parts[2]);
      if (!isNaN(day) && month !== undefined && !isNaN(year)) {
        return new Date(year, month, day);
      }
    }
    return new Date(dateStr);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingEvents = allEvents.filter(event => {
    const eventDate = parseEventDate(event.date);
    return eventDate >= today && !event.isFeatured;
  });
  
  // 3. Минали (датата е в миналото и не са featured)
  const pastEvents = allEvents.filter(event => {
    const eventDate = parseEventDate(event.date);
    // Турнирът от 2026 г. остава в „Актуални новини“, но след началната
    // си дата се вижда и в архива „Минали“.
    return eventDate < today && (!event.isFeatured || event.id === 'event_5');
  });

  const getEventsByTab = () => {
    switch(activeTab) {
      case 'featured':
        return featuredEvents;
      case 'upcoming':
        return upcomingEvents;
      case 'past':
        return pastEvents;
      default:
        return featuredEvents;
    }
  };

  const filteredEvents = getEventsByTab();

  // Измерване на височините
  useEffect(() => {
    setTimeout(() => {
      const newHeights = {};
      let maxHeight = 0;
      
      filteredEvents.forEach((event) => {
        const cardElement = cardRefs.current[event.id];
        if (cardElement) {
          const height = cardElement.clientHeight;
          newHeights[event.id] = height;
          if (height > maxHeight) {
            maxHeight = height;
          }
        }
      });
      
      if (maxHeight > 0) {
        const updatedHeights = {};
        filteredEvents.forEach((event) => {
          updatedHeights[event.id] = maxHeight;
        });
        setHeights(updatedHeights);
      }
    }, 100);
  }, [filteredEvents]);

  // Format date for display
  const formatDate = (dateStr) => {
    const date = parseEventDate(dateStr);
    if (i18n.language === 'bg') {
      const months = ['Януари', 'Февруари', 'Март', 'Април', 'Май', 'Юни', 
                      'Юли', 'Август', 'Септември', 'Октомври', 'Ноември', 'Декември'];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    }
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Add to Google Calendar
  const addToCalendar = (event) => {
    const eventDate = parseEventDate(event.date);
    const title = encodeURIComponent(i18n.language === 'bg' ? event.title_bg : event.title);
    const location = encodeURIComponent(event.location);
    const description = encodeURIComponent(i18n.language === 'bg' ? event.description_bg : event.description);
    
    const formatCalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    };
    
    const startDate = formatCalDate(eventDate);
    const endDate = formatCalDate(new Date(eventDate.getTime() + 3 * 60 * 60 * 1000));
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDate}/${endDate}&details=${description}&location=${location}`;
    
    window.open(googleCalendarUrl, '_blank');
  };

  return (
    <div className="min-h-screen py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-foreground mb-6">
            {t('events.title')}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {t('events.subtitle')}
          </p>
        </motion.div>

        {/* Tabs - 3 таба */}
        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          <button
            onClick={() => setActiveTab('featured')}
            className={`px-8 py-3 font-bold uppercase tracking-wider transition-all ${
              activeTab === 'featured'
                ? 'bg-accent text-accent-foreground'
                : 'bg-secondary text-foreground hover:bg-secondary/80'
            }`}
          >
            {i18n.language === 'bg' ? 'Актуални новини' : 'Current news'}
            <span className="ml-2 text-sm">({featuredEvents.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-8 py-3 font-bold uppercase tracking-wider transition-all ${
              activeTab === 'upcoming'
                ? 'bg-accent text-accent-foreground'
                : 'bg-secondary text-foreground hover:bg-secondary/80'
            }`}
          >
            {i18n.language === 'bg' ? 'Предстоящи' : 'Upcoming'}
            <span className="ml-2 text-sm">({upcomingEvents.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('past')}
            className={`px-8 py-3 font-bold uppercase tracking-wider transition-all ${
              activeTab === 'past'
                ? 'bg-accent text-accent-foreground'
                : 'bg-secondary text-foreground hover:bg-secondary/80'
            }`}
          >
            {i18n.language === 'bg' ? 'Минали' : 'Past'}
            <span className="ml-2 text-sm">({pastEvents.length})</span>
          </button>
        </div>

        {/* Events Grid */}
        {filteredEvents.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-muted-foreground">
              {activeTab === 'featured' 
                ? (i18n.language === 'bg' ? 'Няма актуални събития' : 'No current events')
                : activeTab === 'upcoming'
                  ? (i18n.language === 'bg' ? 'Няма предстоящи събития' : 'No upcoming events')
                  : (i18n.language === 'bg' ? 'Няма минали събития' : 'No past events')
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredEvents.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group"
                style={{ 
                  height: heights[event.id] ? `${heights[event.id]}px` : 'auto'
                }}
              >
                <div className="flex flex-col h-full">
                  <Link to={`/${i18n.language}/events/${event.id}`} className="flex-1">
                    <div className="bg-card border border-border overflow-hidden h-full flex flex-col">
                      <div className="relative aspect-video overflow-hidden">
                        <img
                          src={event.image}
                          alt={i18n.language === 'bg' ? event.title_bg : event.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          style={event.card_image_position ? { objectPosition: event.card_image_position } : undefined}
                        />
                        <div className="absolute top-4 right-4 bg-accent text-accent-foreground px-3 py-1 text-sm font-bold uppercase">
                          {activeTab === 'featured' 
                            ? (i18n.language === 'bg' ? 'АКТУАЛНО' : 'CURRENT')
                            : event.category === 'match' 
                              ? (i18n.language === 'bg' ? 'Мач' : 'Match')
                              : event.category === 'charity' 
                                ? (i18n.language === 'bg' ? 'Благотворителност' : 'Charity')
                                : event.category === 'gala' 
                                  ? (i18n.language === 'bg' ? 'Гала' : 'Gala')
                                  : (i18n.language === 'bg' ? 'Събитие' : 'Event')
                          }
                        </div>
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        <h3 className="font-heading text-xl font-bold text-foreground mb-4 group-hover:text-accent transition-colors">
                          {i18n.language === 'bg' ? event.title_bg : event.title}
                        </h3>
                        <div className="space-y-2 text-muted-foreground mt-auto">
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm">{formatDate(event.date)}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm">{event.location}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                  
                  {/* Add to Calendar Button */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      addToCalendar(event);
                    }}
                    className="w-full mt-2 py-2 border border-border text-foreground hover:bg-secondary transition-colors flex items-center justify-center gap-2 text-sm flex-shrink-0"
                  >
                    <Calendar className="w-4 h-4" />
                    {i18n.language === 'bg' ? 'Добави в календар' : 'Add to Calendar'}
                  </button>
                </div>
                
                {/* Скрит елемент за измерване на реалната височина */}
                <div 
                  ref={el => {
                    if (el && !cardRefs.current[event.id]) {
                      cardRefs.current[event.id] = el.parentElement;
                    }
                  }}
                  className="hidden"
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;